import { useEffect, useState } from "react";
import {
  ClientSafeGovernanceViewV1Schema,
  type ClientSafeGovernanceViewV1,
} from "@enrein/shared-contracts";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

export function toClientVisibleActivityText(value: string): string {
  return value
    .replace(/\bclientLinkId=cl_[A-Za-z0-9_-]+\b/g, "client link")
    .replace(/\bcl_[A-Za-z0-9_-]+\b/g, "client link");
}

export function GovernancePanel({
  api,
  clientLinkId,
  token,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
}) {
  const [view, setView] = useState<ClientSafeGovernanceViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const next = ClientSafeGovernanceViewV1Schema.parse(
          await api.getGovernance(clientLinkId, token),
        );
        if (!cancelled) {
          setView(next);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setView(null);
          setError(
            err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
          );
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, clientLinkId, token]);

  if (error) {
    return (
      <section className="value-card" aria-label="Governance">
        <p className="value-card-eyebrow">Project governance</p>
        <h2 className="value-card-headline">Governance</h2>
        <p className="warning">{error}</p>
      </section>
    );
  }

  if (!view) {
    return (
      <section className="value-card loading-panel" aria-label="Governance">
        <p className="value-card-eyebrow">Project governance</p>
        <h2 className="value-card-headline">Governance</h2>
        <p>Loading your project summary…</p>
      </section>
    );
  }

  return (
    <section className="value-card" aria-label="Governance">
      <p className="value-card-eyebrow">Project governance</p>
      <h2 className="value-card-headline">{view.headline}</h2>
      <span className="value-card-status value-card-status--ready">{view.releaseLabelClientSafe}</span>
      <p>{view.coverageSummary}</p>
      {view.managerExceptionSummary ? <p className="warning">{view.managerExceptionSummary}</p> : null}
      {view.timelineBullets.length > 0 ? (
        <>
          <h3>Recent activity</h3>
          <ul className="activity-list">
            {view.timelineBullets.map((b, index) => (
              <li key={`${b.headline}-${index}`} className="activity-card">
                <p className="activity-card-headline">{b.headline}</p>
                <p className="activity-card-summary">{toClientVisibleActivityText(b.summary)}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {view.responsibilityBullets.length > 0 ? (
        <>
          <h3>Your action items</h3>
          <ul>
            {view.responsibilityBullets.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
      <details>
        <summary>What we do not promise</summary>
        <ul>
          {view.disclaimers.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
